import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Upload, 
  FileSpreadsheet, 
  Globe, 
  FileText, 
  Link as LinkIcon,
  Eye,
  Send,
  Check,
  Loader2,
  AlertTriangle,
  Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const steps = [
  { id: 'name', title: 'Campaign Name', icon: FileText },
  { id: 'domain', title: 'Select Domain', icon: Globe },
  { id: 'template', title: 'Select Template', icon: FileText },
  { id: 'upload', title: 'Upload Excel', icon: Upload },
  { id: 'mapping', title: 'Map Columns', icon: LinkIcon },
  { id: 'preview', title: 'Preview', icon: Eye },
  { id: 'confirm', title: 'Confirm & Send', icon: Send },
];

const CampaignWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [previews, setPreviews] = useState([]);

  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [missingBehavior, setMissingBehavior] = useState('blank');
  const [warmupMode, setWarmupMode] = useState(false);
  const [complianceChecked, setComplianceChecked] = useState(false);
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentData, setAttachmentData] = useState(null);

  useEffect(() => {
    fetchDomains();
    fetchTemplates();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/domains`);
      setDomains(response.data.filter(d => d.verified));
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/campaigns/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFile(file);
      setFileData(response.data);
      
      // Auto-map email column if found
      const emailCol = response.data.headers.find(h => 
        h.toLowerCase().includes('email') || h.toLowerCase() === 'e-mail'
      );
      if (emailCol) {
        setColumnMapping(prev => ({ ...prev, email: emailCol }));
      }
      
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/campaigns/upload-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachmentFile(file);
      setAttachmentData(response.data);
      toast.success(`Attachment "${file.name}" uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (variable, column) => {
    setColumnMapping(prev => ({
      ...prev,
      [variable]: column
    }));
  };

  const fetchPreviews = async (campaignId) => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/preview`);
      setPreviews(response.data.previews);
    } catch (error) {
      console.error('Failed to fetch previews:', error);
    }
  };

  const createCampaign = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/campaigns`, {
        name: campaignName,
        domain_id: selectedDomain.id,
        template_id: selectedTemplate.id,
        column_mapping: columnMapping,
        missing_variable_behavior: missingBehavior,
        warmup_mode: warmupMode,
        from_name: fromName,
        from_email: fromEmail,
        reply_to_email: replyToEmail,
        file_id: fileData.file_id,
        attachment_id: attachmentData?.attachment_id || null
      });
      
      const skipped = response.data.skipped;
      if (skipped && (skipped.suppressed > 0 || skipped.duplicate > 0 || skipped.invalid > 0)) {
        const parts = [];
        if (skipped.suppressed > 0) parts.push(`${skipped.suppressed} suppressed (bounced previously)`);
        if (skipped.duplicate > 0) parts.push(`${skipped.duplicate} duplicates`);
        if (skipped.invalid > 0) parts.push(`${skipped.invalid} invalid emails`);
        toast.info(`Skipped: ${parts.join(', ')}`, { duration: 8000 });
      }
      
      await fetchPreviews(response.data.id);
      setCurrentStep(5); // Move to preview step
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const sendCampaign = async () => {
    // Find the campaign ID from the last created campaign
    const campaigns = await axios.get(`${API_URL}/campaigns`);
    const latestCampaign = campaigns.data.find(c => c.name === campaignName);
    
    if (!latestCampaign) {
      toast.error('Campaign not found');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/campaigns/${latestCampaign.id}/send`);
      toast.success('Campaign sending started!');
      navigate(`/campaigns/${latestCampaign.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return campaignName.trim().length > 0;
      case 1: return selectedDomain !== null;
      case 2: return selectedTemplate !== null;
      case 3: return fileData !== null;
      case 4: return columnMapping.email;
      case 5: return true;
      case 6: return complianceChecked;
      default: return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 4) {
      try {
        await createCampaign();
      } catch (error) {
        // Error already shown by createCampaign toast
        return;
      }
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Extract variables from template content (supports spaces in variable names like {custom 1})
  const templateVariables = React.useMemo(() => {
    if (!selectedTemplate) return [];
    const stored = selectedTemplate.variables || [];
    // Also extract from subject + body as fallback
    const text = (selectedTemplate.subject || '') + ' ' + (selectedTemplate.html_body || '');
    const matches = text.match(/\{([^{}]+)\}/g) || [];
    const extracted = matches.map(m => m.slice(1, -1));
    // Merge stored + extracted, deduplicate
    const all = [...new Set([...stored, ...extracted])];
    return all;
  }, [selectedTemplate]);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="campaign-wizard">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Create Campaign</h1>
          <p className="text-muted-foreground text-sm">Step {currentStep + 1} of {steps.length}</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between overflow-x-auto pb-2">
        {steps.map((step, idx) => (
          <div 
            key={step.id}
            className={`flex items-center ${idx < steps.length - 1 ? 'flex-1' : ''}`}
          >
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              idx === currentStep 
                ? 'bg-primary text-primary-foreground' 
                : idx < currentStep 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                  : 'bg-muted text-muted-foreground'
            }`}>
              {idx < currentStep ? (
                <Check className="w-4 h-4" />
              ) : (
                <step.icon className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{step.title}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${
                idx < currentStep ? 'bg-emerald-500' : 'bg-muted'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 0: Campaign Name & Sender Info */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div>
                <CardTitle className="mb-2">Campaign Details</CardTitle>
                <CardDescription>Set up your campaign name and sender information</CardDescription>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name</Label>
                <Input
                  id="name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="e.g., Q1 Outreach - Tech Founders"
                  className="max-w-md"
                  data-testid="campaign-name-input"
                />
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Sender Information</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fromName">Your Name</Label>
                    <Input
                      id="fromName"
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                      placeholder="e.g., Tanuj Khosla"
                      data-testid="from-name-input"
                    />
                    <p className="text-xs text-muted-foreground">Recipients will see this name</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fromEmail">From Email (prefix)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="fromEmail"
                        value={fromEmail}
                        onChange={(e) => setFromEmail(e.target.value)}
                        placeholder="e.g., tanuj"
                        className="max-w-[150px]"
                        data-testid="from-email-input"
                      />
                      <span className="text-muted-foreground">@yourdomain.com</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Domain will be added from step 2</p>
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label htmlFor="replyTo">Reply-To Email</Label>
                  <Input
                    id="replyTo"
                    type="email"
                    value={replyToEmail}
                    onChange={(e) => setReplyToEmail(e.target.value)}
                    placeholder="e.g., yourpersonal@gmail.com"
                    className="max-w-md"
                    data-testid="reply-to-input"
                  />
                  <p className="text-xs text-muted-foreground">When recipients reply, emails go HERE (your personal inbox)</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Select Domain */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <CardTitle className="mb-2">Select Sending Domain</CardTitle>
                <CardDescription>Choose a verified domain to send from</CardDescription>
              </div>
              {domains.length > 0 ? (
                <div className="grid gap-3">
                  {domains.map(domain => (
                    <div
                      key={domain.id}
                      onClick={() => setSelectedDomain(domain)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedDomain?.id === domain.id 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`domain-option-${domain.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-medium">{domain.domain}</p>
                            <p className="text-sm text-muted-foreground">
                              {domain.daily_sent_count || 0} / {domain.daily_cap} emails sent today
                            </p>
                          </div>
                        </div>
                        {selectedDomain?.id === domain.id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">No verified domains found</p>
                  <Button variant="link" onClick={() => navigate('/domains')}>
                    Add a domain first
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Template */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <CardTitle className="mb-2">Select Email Template</CardTitle>
                <CardDescription>Choose the template for your campaign</CardDescription>
              </div>
              {templates.length > 0 ? (
                <div className="grid gap-3">
                  {templates.map(template => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`template-option-${template.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{template.name}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            Subject: {template.subject}
                          </p>
                          {template.variables?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {template.variables.slice(0, 4).map(v => (
                                <Badge key={v} variant="outline" className="text-xs">{`{${v}}`}</Badge>
                              ))}
                              {template.variables.length > 4 && (
                                <Badge variant="outline" className="text-xs">+{template.variables.length - 4}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No templates found</p>
                  <Button variant="link" onClick={() => navigate('/templates')}>
                    Create a template first
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Upload Excel */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <CardTitle className="mb-2">Upload Recipient Data</CardTitle>
                <CardDescription>Upload an Excel file with your recipients</CardDescription>
              </div>
              
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                {!uploadedFile ? (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                    <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">Click to upload Excel file</p>
                    <p className="text-sm text-muted-foreground">.xlsx or .xls files</p>
                  </label>
                ) : (
                  <div>
                    <Check className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="font-medium">{uploadedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {fileData?.row_count} rows · {fileData?.headers?.length} columns
                    </p>
                    <Button 
                      variant="link" 
                      onClick={() => { setUploadedFile(null); setFileData(null); }}
                    >
                      Upload different file
                    </Button>
                  </div>
                )}
              </div>

              {fileData?.sample_data?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Preview (first {fileData.sample_data.length} rows)</Label>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-sm border rounded-lg">
                      <thead className="bg-muted/50">
                        <tr>
                          {fileData.headers.map(h => (
                            <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fileData.sample_data.map((row, idx) => (
                          <tr key={idx} className="border-t">
                            {fileData.headers.map(h => (
                              <td key={h} className="px-3 py-2 truncate max-w-[150px]">{row[h]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Attachment Upload */}
              <div className="mt-6">
                <Label className="font-medium">Attachment (optional)</Label>
                <p className="text-sm text-muted-foreground mb-2">Attach a file that will be sent with every email (max 10MB)</p>
                <div className="border rounded-lg p-4">
                  {!attachmentData ? (
                    <label className="cursor-pointer flex items-center gap-3">
                      <input
                        type="file"
                        onChange={handleAttachmentUpload}
                        className="hidden"
                        data-testid="attachment-upload-input"
                      />
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to attach a file (PDF, image, doc, etc.)</span>
                    </label>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm font-medium">{attachmentData.filename}</span>
                        <span className="text-xs text-muted-foreground">({(attachmentData.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setAttachmentFile(null); setAttachmentData(null); }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Map Columns */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <CardTitle className="mb-2">Map Template Variables</CardTitle>
                <CardDescription>Connect your template variables to Excel columns</CardDescription>
              </div>
              
              <div className="space-y-4">
                {/* Email mapping (required) */}
                <div className="p-4 rounded-lg border border-primary/50 bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge>Required</Badge>
                      <span className="font-medium">Email Address</span>
                    </div>
                    <Select
                      value={columnMapping.email || ''}
                      onValueChange={(val) => handleMappingChange('email', val)}
                    >
                      <SelectTrigger className="w-[200px]" data-testid="email-column-select">
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        {fileData?.headers?.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Template variables */}
                {templateVariables.map(variable => (
                  <div key={variable} className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{`{${variable}}`}</Badge>
                    </div>
                    <Select
                      value={columnMapping[variable] || '__none__'}
                      onValueChange={(val) => handleMappingChange(variable, val === '__none__' ? '' : val)}
                    >
                      <SelectTrigger className="w-[200px]" data-testid={`mapping-${variable}`}>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Not mapped --</SelectItem>
                        {fileData?.headers?.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}

                {/* Missing variable behavior */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <Label className="font-medium">When a variable is missing</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="missing"
                        value="blank"
                        checked={missingBehavior === 'blank'}
                        onChange={(e) => setMissingBehavior(e.target.value)}
                        className="accent-primary"
                      />
                      <span className="text-sm">Insert blank</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="missing"
                        value="skip"
                        checked={missingBehavior === 'skip'}
                        onChange={(e) => setMissingBehavior(e.target.value)}
                        className="accent-primary"
                      />
                      <span className="text-sm">Skip recipient</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Preview */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div>
                <CardTitle className="mb-2">Preview Emails</CardTitle>
                <CardDescription>Review sample personalized emails before sending</CardDescription>
              </div>
              
              {previews.length > 0 ? (
                <div className="space-y-4">
                  {previews.map((preview, idx) => (
                    <div key={idx} className="p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline">Sample {idx + 1}</Badge>
                        <span className="text-sm text-muted-foreground">To: {preview.to}</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Subject</Label>
                          <p className="font-medium">{preview.subject}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Body</Label>
                          <div 
                            className="mt-1 p-3 rounded bg-white dark:bg-slate-950 border text-sm"
                            dangerouslySetInnerHTML={{ __html: preview.html_body }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Loading previews...
                </div>
              )}
            </div>
          )}

          {/* Step 6: Confirm & Send */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <CardTitle className="mb-2">Confirm & Send</CardTitle>
                <CardDescription>Review your campaign settings before sending</CardDescription>
              </div>

              {/* Summary */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Campaign</p>
                  <p className="font-medium">{campaignName}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Domain</p>
                  <p className="font-medium">{selectedDomain?.domain}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Template</p>
                  <p className="font-medium">{selectedTemplate?.name}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Recipients</p>
                  <p className="font-medium">{fileData?.row_count || 0}</p>
                </div>
              </div>

              {/* Sender Info Summary */}
              <div className="p-4 rounded-lg border bg-primary/5">
                <h4 className="font-medium mb-3">Sender Information</h4>
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-medium">
                      {fromName ? `${fromName} <${fromEmail || 'noreply'}@${selectedDomain?.domain}>` : `${fromEmail || 'noreply'}@${selectedDomain?.domain}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Replies go to:</span>
                    <span className="font-medium">{replyToEmail || `${fromEmail || 'noreply'}@${selectedDomain?.domain}`}</span>
                  </div>
                </div>
              </div>

              {/* Warmup Mode */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium">Warmup Mode</p>
                    <p className="text-sm text-muted-foreground">
                      Send slower (50/day, 20-60s delays) to build domain reputation
                    </p>
                  </div>
                </div>
                <Switch
                  checked={warmupMode}
                  onCheckedChange={setWarmupMode}
                  data-testid="warmup-mode-toggle"
                />
              </div>

              {/* Compliance Checkbox */}
              <div className="p-4 rounded-lg border border-amber-500/50 bg-amber-500/5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={complianceChecked}
                    onCheckedChange={setComplianceChecked}
                    className="mt-1"
                    data-testid="compliance-checkbox"
                  />
                  <div>
                    <p className="font-medium">I confirm compliance</p>
                    <p className="text-sm text-muted-foreground">
                      I confirm that all recipients have given consent to receive emails, 
                      and this campaign complies with applicable anti-spam laws (CAN-SPAM, GDPR, etc.)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed() || loading}
            data-testid="next-step-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={sendCampaign}
            disabled={!canProceed() || loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="send-campaign-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Start Sending
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CampaignWizard;
